import React, { useState, useEffect } from 'react';
import { clientAPI, sprintAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Plus,
  Loader2,
  Calendar,
  FolderKanban,
  BarChart3,
  ChevronRight,
  Edit,
} from 'lucide-react';

const Projects = () => {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [editProjectDialogOpen, setEditProjectDialogOpen] = useState(false);
  const [sprintDialogOpen, setSprintDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sprints, setSprints] = useState([]);
  const [newProject, setNewProject] = useState({
    name: '',
    code: '',
    description: '',
  });
  const [editProject, setEditProject] = useState({
    id: '',
    name: '',
    code: '',
  });
  const [newSprint, setNewSprint] = useState({
    name: '',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchSprints();
    }
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const response = await clientAPI.getClients();
      setProjects(response.data);
      if (response.data.length > 0 && !selectedProject) {
        setSelectedProject(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSprints = async () => {
    if (!selectedProject) return;
    try {
      const response = await sprintAPI.getSprints({ project_id: selectedProject.id });
      setSprints(response.data);
    } catch (error) {
      setSprints([]);
    }
  };

  const handleCreateProject = async () => {
    if (!newProject.name || !newProject.code) return;
    setCreating(true);
    try {
      await clientAPI.createClient({
        name: newProject.name,
        code: newProject.code.toUpperCase(),
        departments: ['Engineering', 'Product', 'QA', 'Operations'],
      });
      setProjectDialogOpen(false);
      setNewProject({ name: '', code: '', description: '' });
      fetchProjects();
    } catch (error) {
      console.error('Failed to create project:', error);
      alert(error.response?.data?.detail || 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateSprint = async () => {
    if (!newSprint.name || !newSprint.start_date || !newSprint.end_date || !selectedProject) return;
    try {
      await sprintAPI.createSprint({
        ...newSprint,
        project_id: selectedProject.id
      });
      setSprintDialogOpen(false);
      setNewSprint({ name: '', start_date: '', end_date: '' });
      fetchSprints();
    } catch (error) {
      console.error('Failed to create sprint:', error);
    }
  };

  const handleEditProject = (project) => {
    setEditProject({ id: project.id, name: project.name, code: project.code });
    setEditProjectDialogOpen(true);
  };

  const handleSaveEditProject = async () => {
    if (!editProject.name || !editProject.code) return;
    setSaving(true);
    try {
      await clientAPI.updateClient(editProject.id, {
        name: editProject.name,
        code: editProject.code.toUpperCase(),
      });
      setEditProjectDialogOpen(false);
      // Refresh projects and update selection
      const response = await clientAPI.getClients();
      setProjects(response.data);
      const updated = response.data.find(p => p.id === editProject.id);
      if (updated) setSelectedProject(updated);
    } catch (error) {
      console.error('Failed to update project:', error);
      alert(error.response?.data?.detail || 'Failed to update project');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="projects-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl lg:text-4xl font-black tracking-tight text-slate-900">
            Projects
          </h1>
          <p className="text-slate-500 mt-1">
            Manage projects, sprints, and view timelines
          </p>
        </div>

        <button
          onClick={() => setProjectDialogOpen(true)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium bg-[#002FA7] text-white hover:bg-[#002585] transition-colors"
          data-testid="new-project-btn"
        >
          <Plus className="h-4 w-4" />
          New Project
        </button>
      </div>

      {projects.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-lg p-12 text-center bg-white">
          <FolderKanban className="h-12 w-12 mx-auto text-slate-300 mb-4" />
          <p className="text-slate-400">No projects yet. Create your first project!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Project List */}
          <div className="space-y-2">
            <h3 className="font-semibold text-sm text-slate-500 uppercase tracking-wider px-2">
              All Projects ({projects.length})
            </h3>
            {projects.map((project) => (
              <button
                key={project.id}
                onClick={() => setSelectedProject(project)}
                className={`w-full text-left p-4 border rounded-lg transition-all ${
                  selectedProject?.id === project.id
                    ? 'border-[#002FA7] bg-[#eef2ff]'
                    : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
                data-testid={`project-${project.id}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm text-slate-800">{project.name}</span>
                  <div className="flex items-center gap-1">
                    <span
                      role="button"
                      onClick={(e) => { e.stopPropagation(); handleEditProject(project); }}
                      className="p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600"
                      data-testid={`edit-project-list-${project.id}`}
                    >
                      <Edit className="h-3.5 w-3.5" />
                    </span>
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline" className="rounded-full text-xs border-slate-200 text-slate-500">
                    {project.code}
                  </Badge>
                </div>
              </button>
            ))}
          </div>

          {/* Project Details */}
          <div className="lg:col-span-3">
            {selectedProject ? (
              <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
                <div className="p-6 border-b border-slate-200">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="font-heading text-2xl font-bold text-slate-900">{selectedProject.name}</h2>
                        <button
                          onClick={() => handleEditProject(selectedProject)}
                          className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                          title="Edit project"
                          data-testid="edit-project-btn"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="flex items-center gap-3 mt-2">
                        <Badge variant="outline" className="rounded-full border-slate-200 text-slate-600">
                          Code: {selectedProject.code}
                        </Badge>
                        {selectedProject.departments?.length > 0 && (
                          <span className="text-sm text-slate-500">
                            {selectedProject.departments.length} departments
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => setSprintDialogOpen(true)}
                      className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors"
                      data-testid="add-sprint-btn"
                    >
                      <Plus className="h-4 w-4" />
                      Add Sprint
                    </button>
                  </div>
                </div>

                {/* Departments */}
                {selectedProject.departments?.length > 0 && (
                  <div className="px-6 py-4 border-b border-slate-100">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Departments / Modules</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedProject.departments.map((dept) => (
                        <Badge key={dept} variant="secondary" className="rounded-full bg-slate-100 text-slate-600 border-0">
                          {dept}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Gantt Chart */}
                <div className="p-6">
                  <h3 className="font-semibold text-slate-800 flex items-center gap-2 mb-4">
                    <BarChart3 className="h-4 w-4 text-blue-500" />
                    Sprint Timeline
                  </h3>

                  {sprints.length > 0 ? (
                    <div className="overflow-x-auto">
                      <div className="min-w-[600px] space-y-2">
                        {sprints.map((sprint) => (
                          <div key={sprint.id} className="flex items-center gap-4" data-testid={`sprint-item-${sprint.id}`}>
                            <div className="w-40 flex-shrink-0">
                              <span className="text-sm font-medium text-slate-600">
                                {sprint.name}
                              </span>
                            </div>
                            <div className="flex-1 relative h-8 bg-slate-100 rounded-md overflow-hidden">
                              <div
                                className="absolute top-1 h-6 rounded-md bg-[#60A5FA]"
                                style={{
                                  left: '0',
                                  width: `${Math.max(40, 100)}px`,
                                }}
                              />
                            </div>
                            <div className="w-48 text-right text-xs text-slate-400">
                              {formatDate(sprint.start_date)} - {formatDate(sprint.end_date)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-400">
                      No sprints yet. Add a sprint to see the timeline.
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="border border-dashed border-slate-300 rounded-lg p-12 text-center bg-white">
                <p className="text-slate-400">Select a project to view details</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Project Dialog */}
      <Dialog open={projectDialogOpen} onOpenChange={setProjectDialogOpen}>
        <DialogContent className="sm:max-w-[480px] border border-slate-200 bg-white text-slate-800" data-testid="project-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold text-slate-800">Create New Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Project Name *</label>
              <Input
                value={newProject.name}
                onChange={(e) => setNewProject(prev => ({ ...prev, name: e.target.value }))}
                className="mt-1 rounded-md bg-white border-slate-200 text-slate-800"
                placeholder="e.g., Isthara ERP"
                data-testid="project-name-input"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Project Code * (Short identifier)</label>
              <Input
                value={newProject.code}
                onChange={(e) => setNewProject(prev => ({ ...prev, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 5) }))}
                className="mt-1 rounded-md bg-white border-slate-200 text-slate-800"
                placeholder="e.g., IST, SMI, CHS"
                maxLength={5}
                data-testid="project-code-input"
              />
            </div>
            <button
              onClick={handleCreateProject}
              disabled={creating || !newProject.name || !newProject.code}
              className="w-full py-2.5 rounded-md text-sm font-semibold bg-[#002FA7] text-white hover:bg-[#002585] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="create-project-btn"
            >
              {creating && <Loader2 className="h-4 w-4 animate-spin" />}
              Create Project
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Sprint Dialog */}
      <Dialog open={sprintDialogOpen} onOpenChange={setSprintDialogOpen}>
        <DialogContent className="sm:max-w-[480px] border border-slate-200 bg-white text-slate-800" data-testid="sprint-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold text-slate-800">Create New Sprint</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Sprint Name *</label>
              <Input
                value={newSprint.name}
                onChange={(e) => setNewSprint(prev => ({ ...prev, name: e.target.value }))}
                className="mt-1 rounded-md bg-white border-slate-200 text-slate-800"
                placeholder="Sprint name"
                data-testid="sprint-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Start Date *</label>
                <Input
                  type="date"
                  value={newSprint.start_date}
                  onChange={(e) => setNewSprint(prev => ({ ...prev, start_date: e.target.value }))}
                  className="mt-1 rounded-md bg-white border-slate-200 text-slate-800"
                  data-testid="sprint-start-input"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">End Date *</label>
                <Input
                  type="date"
                  value={newSprint.end_date}
                  onChange={(e) => setNewSprint(prev => ({ ...prev, end_date: e.target.value }))}
                  className="mt-1 rounded-md bg-white border-slate-200 text-slate-800"
                  data-testid="sprint-end-input"
                />
              </div>
            </div>
            <button
              onClick={handleCreateSprint}
              disabled={!newSprint.name || !newSprint.start_date || !newSprint.end_date}
              className="w-full py-2.5 rounded-md text-sm font-semibold bg-[#002FA7] text-white hover:bg-[#002585] transition-colors disabled:opacity-50"
              data-testid="create-sprint-btn"
            >
              Create Sprint
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog open={editProjectDialogOpen} onOpenChange={setEditProjectDialogOpen}>
        <DialogContent className="sm:max-w-[480px] border border-slate-200 bg-white text-slate-800" data-testid="edit-project-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold text-slate-800">Edit Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Project Name *</label>
              <Input
                value={editProject.name}
                onChange={(e) => setEditProject(prev => ({ ...prev, name: e.target.value }))}
                className="mt-1 rounded-md bg-white border-slate-200 text-slate-800"
                placeholder="Project name"
                data-testid="edit-project-name-input"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Project Code *</label>
              <Input
                value={editProject.code}
                onChange={(e) => setEditProject(prev => ({ ...prev, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 5) }))}
                className="mt-1 rounded-md bg-white border-slate-200 text-slate-800"
                placeholder="e.g., IST, SMI"
                maxLength={5}
                data-testid="edit-project-code-input"
              />
            </div>
            <button
              onClick={handleSaveEditProject}
              disabled={saving || !editProject.name || !editProject.code}
              className="w-full py-2.5 rounded-md text-sm font-semibold bg-[#002FA7] text-white hover:bg-[#002585] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="save-edit-project-btn"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Save Changes
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Projects;
